import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../services/AuthService";
import useAuth from "../hooks/useAuth";
import "../styles/VerifyOTP.css";
import Swal from "sweetalert2";

// Helper function to mask mobile number (show last 4 digits)
const maskMobileNumber = (mobile) => {
  if (!mobile) return "+91 xxxxxx0000";
  
  // Remove any non-digit characters
  const digitsOnly = mobile.replace(/\D/g, '');
  
  // If mobile number is less than 4 digits, return as is
  if (digitsOnly.length <= 4) {
    return `+91 ${digitsOnly}`;
  }
  
  // Show last 4 digits, mask the rest
  const last4Digits = digitsOnly.slice(-4);
  const maskedPart = 'x'.repeat(Math.max(digitsOnly.length - 4, 0));
  
  return `+91 ${maskedPart}${last4Digits}`;
};

const VerifyOTP = () => {
  const RESEND_COOLDOWN_SECONDS = 60;
  const OTP_RESEND_USER_KEY = "otp_resend_username";
  const OTP_RESEND_PASS_KEY = "otp_resend_password";
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [timer, setTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);
  const location = useLocation();
  const { verifyOtp, role, token, login } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const creationMessage = location.state?.creationMessage;
  const username = location.state?.username || sessionStorage.getItem(OTP_RESEND_USER_KEY) || "";
  const password = location.state?.password || sessionStorage.getItem(OTP_RESEND_PASS_KEY) || "";
  const [currentOtpData, setCurrentOtpData] = useState(location.state?.otpData);

  // Redirect if otpData not available
  useEffect(() => {
    if (!currentOtpData) {
      navigate("/login");
    }
  }, [currentOtpData, navigate]);

  // Redirect MSIL user if already authenticated
  useEffect(() => {
    if (role === "MSIL" && token) {
      navigate("/AdminHome");
    }
  }, [role, token, navigate]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  // Timer logic
  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(countdown);
    } else {
      setResendEnabled(true);
    }
  }, [timer]);

  const handleResendOtp = async () => {
    if (isResending) return;
    const effectiveUser =
      username ||
      currentOtpData?.userName ||
      currentOtpData?.username ||
      "";
    const effectivePassword = password || sessionStorage.getItem(OTP_RESEND_PASS_KEY) || "";
    if (!effectiveUser?.trim() || !effectivePassword?.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Cannot resend OTP",
        text: "Missing login details for resend. Please try login again.",
        confirmButtonText: "OK",
      });
      return;
    }
    setIsResending(true);
    try {
      const resendResponse = await AuthService.login(effectiveUser.trim(), effectivePassword);
      // Keep cached credentials fresh for further resend attempts in the same browser session.
      sessionStorage.setItem(OTP_RESEND_USER_KEY, effectiveUser.trim());
      sessionStorage.setItem(OTP_RESEND_PASS_KEY, effectivePassword);
      setCurrentOtpData((prev) => ({
        ...prev,
        requestId: resendResponse?.requestId || prev?.requestId,
        mobile: resendResponse?.mobile || prev?.mobile,
        application: resendResponse?.application || prev?.application,
        userName: resendResponse?.userName || prev?.userName,
      }));
      setOtp(new Array(6).fill(""));
      setTimer(RESEND_COOLDOWN_SECONDS);
      setResendEnabled(false);

      Swal.fire({
        icon: "success",
        title: "OTP Resent",
        text: "OTP has been resent to your registered mobile number.",
        timer: 2000,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      inputRefs.current[0]?.focus();
    } catch (error) {
      const msg =
        (error && error.message) ||
        "Failed to resend OTP. Please try again.";
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: msg,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  const enteredOtp = otp.join("");

  try {
    const payload = {
      otp: enteredOtp,
      requestId: currentOtpData.requestId,
      mobile: currentOtpData.mobile,
      application: currentOtpData.application,
      email: currentOtpData.email,
      userName: currentOtpData.userName || username,
      password: currentOtpData.password || password || sessionStorage.getItem(OTP_RESEND_PASS_KEY) || "",
    };

    // ✅ Call verifyOtp, which also calls /validate internally
    const { token, dealerData } = await AuthService.verifyOtp(payload);

    // ✅ Mark OTP as verified and store token/role
    verifyOtp();
    login(token);
    sessionStorage.removeItem(OTP_RESEND_USER_KEY);
    sessionStorage.removeItem(OTP_RESEND_PASS_KEY);

    Swal.fire({
      icon: "success",
      title: "OTP Verified Successfully!",
      showConfirmButton: false,
      timer: 1500,
    });

    setTimeout(() => {
      if (
        sessionStorage.getItem("role") === "MASS_EXECUTIVE" ||
        sessionStorage.getItem("role") === "MASS_ADMIN"
      ) {
        navigate("/home");
      } else if (role === "MSIL") {
        navigate("/AdminHome");
      } else {
        navigate("/home");
      }
    }, 1500);
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Invalid OTP",
      text: "Please try again!",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    setOtp(new Array(6).fill(""));
    inputRefs.current[0]?.focus();
    setIsSubmitting(false);
  }
};


  // Don’t render for MSIL user
  if (role === "MSIL") {
    return null;
  }

  return (
    <div className="verify-otp-card">
      <h1 className="text-2xl font-semibold text-center mb-6">Verify OTP</h1>
      <div className="otp-sent-message mb-6" role="alert">
        <span className="d-flex align-items-center" style={{ gap: "10px" }}>
          <i className="fas fa-check-circle mr-2"></i>
          {creationMessage
            ? creationMessage
            : `OTP has been sent to your registered Mobile number ${maskMobileNumber(currentOtpData?.mobile)}`}
        </span>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="otp-input-group" style={{ marginBottom: "0px" }}>
          <label className="form-label">
            Enter OTP <span className="required-field">*</span>
          </label>
          <div className="otp-inputs">
            {otp.map((data, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                className="otp-input"
                value={data}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={handleFocus}
                ref={(el) => (inputRefs.current[index] = el)}
              />
            ))}
          </div>
        </div>
        <div className="resend-otp-section mb-4">
          {resendEnabled ? (
            <button
              type="button"
              onClick={handleResendOtp}
              className="resend-button"
              disabled={isResending}
            >
              {isResending ? "Resending..." : "Resend OTP"}
            </button>
          ) : (
            <span className="timer-text">
              <span style={{ color: "#004FBD" }}>
                Resend OTP in {Math.floor(timer / 60)
                  .toString()
                  .padStart(2, "0")}:{(timer % 60).toString().padStart(2, "0")}s
              </span>
            </span>
          )}
        </div>
        <div className="verify-button-container">
          <button
            type="submit"
            className="verify-button"
            disabled={isSubmitting}
          >
            Verify OTP
          </button>
        </div>
      </form>
    </div>
  );
};

export default VerifyOTP;
